import type {
  AddStaffFormData,
  AddStaffValidationRequirements,
} from "./addStaffPanel.contract";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "../shared/staffEmploymentFieldVisibility.logic";
import { hasValue } from "../shared/staffInputValidation.logic";

export type AddStaffSectionName =
  | "personal-details"
  | "contract"
  | "terms"
  | "vacation"
  | "payment";

export type AddStaffSectionCompletion = Readonly<
  Record<AddStaffSectionName, boolean>
>;

interface SectionCompletionParams {
  readonly formData: AddStaffFormData;
  readonly requirements: AddStaffValidationRequirements;
}

interface VisibleSectionsParams {
  readonly employmentType: string | undefined;
  readonly salaryType: string | undefined;
}

/**
 * The sections a panel shows, in the order they are rendered. The contract
 * terms section only exists for employment types that have contract terms, and
 * the vacation section only for salary models that earn vacation days.
 */
export const getVisibleAddStaffSections = ({
  employmentType,
  salaryType,
}: VisibleSectionsParams): AddStaffSectionName[] => {
  const sections: AddStaffSectionName[] = ["personal-details", "contract"];
  if (getEmploymentFieldVisibility(employmentType).showContractTerms) {
    sections.push("terms");
  }
  if (getVacationFieldVisibility(salaryType).showVacationEarningDays) {
    sections.push("vacation");
  }
  sections.push("payment");
  return sections;
};

/**
 * Whether every field a section marks as mandatory has been answered. This is
 * what the completed badge speaks for — that nothing in the section is still
 * waiting on the user, not that what they entered will be accepted. The submit
 * button speaks for that, from `getAddStaffValidationState`.
 *
 * Requirements already follow field visibility, so a field the employment type
 * hides is never waited on.
 */
export const getAddStaffSectionCompletion = ({
  formData,
  requirements,
}: SectionCompletionParams): AddStaffSectionCompletion => {
  return {
    "personal-details":
      hasValue(formData.phoneNumber) &&
      hasValue(formData.firstName) &&
      hasValue(formData.lastName) &&
      (!requirements.isEmployeeNumberRequired ||
        hasValue(formData.employeeNumber)),
    contract:
      hasValue(formData.role) &&
      formData.teamIds.length > 0 &&
      hasValue(formData.employmentType) &&
      Boolean(formData.startDate) &&
      (!requirements.isEndDateRequired || Boolean(formData.endDate)) &&
      (!requirements.isEmployeeCategoryRequired ||
        hasValue(formData.employeeCategory)) &&
      (!requirements.isSalaryModelRequired || hasValue(formData.salaryType)) &&
      (!requirements.isSalaryAmountRequired ||
        hasValue(formData.salaryAmount)) &&
      (!requirements.isWorkPercentageRequired ||
        hasValue(formData.workPercentage)) &&
      (!requirements.isTaxTableRequired || hasValue(formData.taxTable)),
    // Contract terms are optional to a field, so there is never anything owed.
    terms: true,
    vacation:
      hasValue(formData.maxSickLeaveDays) &&
      hasValue(formData.maxCareOfChildDays) &&
      hasValue(formData.maxParentalLeaveDays) &&
      hasValue(formData.advanceVacationDays),
    payment:
      !requirements.isPaymentTypeRequired || hasValue(formData.paymentType),
  };
};
